import { Input, type InputProps } from 'components/Form/Input/Input';
import React from 'react';

interface NumberInputProps extends InputProps {
    label?: string;
    error?: boolean;
    integerOnly?: boolean;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
    (props: NumberInputProps, ref) => {
        const inputRef = React.useRef<HTMLInputElement>(null);

        React.useImperativeHandle(ref, () => inputRef.current!, [inputRef]);

        const maxLength = props.inputProps?.maxLength;

        const pasteHandler = (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();

            const text = event.clipboardData.getData('text');
            let number = Number(text);

            if (props.integerOnly) {
                number = Math.floor(number);
            }

            let value = number.toString();
            if (maxLength && value.length > maxLength) {
                value = value.slice(0, maxLength);
            }

            props.onChange?.({
                ...event,
                target: {
                    ...(inputRef.current as HTMLInputElement),
                    value: value ?? null
                }
            });
        };

        const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === '+' || event.key === '-') {
                event.preventDefault();
            }

            if (
                props.integerOnly &&
                (event.key === '.' || event.key === ',' || event.key === 'e' || event.key === 'E')
            ) {
                event.preventDefault();
            }

            // Prevent typing if maxLength is reached (allow backspace, delete, arrow keys, etc.)
            if (maxLength && inputRef.current) {
                const currentValue = inputRef.current.value || '';
                const isNumberKey = /^[0-9]$/.test(event.key);
                const isControlKey = [
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Tab',
                    'Home',
                    'End'
                ].includes(event.key);
                const hasSelection =
                    inputRef.current.selectionStart !== inputRef.current.selectionEnd;

                // Allow typing if text is selected (will replace selection) or if under maxLength
                if (
                    isNumberKey &&
                    currentValue.length >= maxLength &&
                    !isControlKey &&
                    !hasSelection
                ) {
                    event.preventDefault();
                }
            }

            props.onKeyDown?.(event);
        };

        const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (maxLength) {
                const value = event.target.value;
                if (value.length > maxLength) {
                    event.target.value = value.slice(0, maxLength);
                }
            }
            props.onChange?.(event);
        };

        return (
            <Input
                {...props}
                type="number"
                inputMode="numeric"
                ref={inputRef}
                inputProps={{ ...props.inputProps, pattern: '[0-9]*' }}
                onPaste={pasteHandler}
                onKeyDown={keyDownHandler}
                onChange={onChangeHandler}
            />
        );
    }
);

NumberInput.displayName = 'NumberInput';
